/**
 * Thread notification settings row (ticket 3531, upstream pin e3e7cc3f, MIT).
 * Adapted from `apps/web/src/components/settings/NotificationSettings.tsx`:
 * the permission-request flow, the secure-context guard and the audio unlock
 * are upstream's; the row renders with forge's settings primitives and
 * settings hooks instead of the upstream scoped-settings hook. The in-app
 * toast toggle ships as its own row below the mode select.
 *
 * @module NotificationSettings
 */
import { useState } from "react";

import type { NotificationMode } from "@cmmd-forge/contracts/settings";

import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import {
  hasDesktopNotifications,
  hasNotificationSound,
  NOTIFICATION_MODE_LABELS,
  unlockNotificationAudio,
} from "../../threadNotifications";
import { Switch } from "../ui/switch";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { searchableSetting } from "./settingsSearch";
import { SettingsRow } from "./settingsLayout";

/** Select values outside the union are unreachable but must be narrowed. */
function isNotificationMode(value: string | null): value is NotificationMode {
  return value !== null && Object.hasOwn(NOTIFICATION_MODE_LABELS, value);
}

/**
 * Ask for desktop notification permission when the mode needs it. Returns the
 * message explaining a refusal, or null when the mode may be applied.
 */
async function requestDesktopPermission(value: NotificationMode): Promise<string | null> {
  if (!hasDesktopNotifications(value)) return null;
  if (typeof Notification === "undefined" || !window.isSecureContext) {
    return "Notifications need a supported browser over HTTPS, or the desktop app. Sound only is still available.";
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return "Allow notifications in your browser or system settings, then choose this option again. Sound only is still available.";
    }
  } catch {
    return "Notifications are unavailable in this browser. Sound only is still available.";
  }
  return null;
}

export function NotificationSettings() {
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();
  const [permissionMessage, setPermissionMessage] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);

  return (
    <>
      <SettingsRow
        {...searchableSetting("general-notifications")}
        description={
          permissionMessage ??
          "System alerts when a thread finishes, fails, or needs input or approval. Applies to this device while Forge is open."
        }
        control={
          <Select
            value={settings.notificationMode}
            disabled={requesting}
            onValueChange={async (value) => {
              if (!isNotificationMode(value)) return;
              setPermissionMessage(null);
              if (hasNotificationSound(value)) unlockNotificationAudio();
              if (hasDesktopNotifications(value)) {
                setRequesting(true);
                try {
                  const message = await requestDesktopPermission(value);
                  if (message) {
                    setPermissionMessage(message);
                    return;
                  }
                } finally {
                  setRequesting(false);
                }
              }
              updateSettings({ notificationMode: value });
            }}
          >
            <SelectTrigger className="w-full sm:w-56" aria-label="Thread notifications" size="sm">
              <SelectValue>{NOTIFICATION_MODE_LABELS[settings.notificationMode]}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="end" alignItemWithTrigger={false}>
              {Object.entries(NOTIFICATION_MODE_LABELS).map(([value, label]) => (
                <SelectItem hideIndicator key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        }
      />
      <SettingsRow
        {...searchableSetting("general-in-app-notifications")}
        description="Show a toast in this window when a background thread needs attention. Independent of the mode above."
        control={
          <Switch
            aria-label="In-app notifications"
            checked={settings.inAppNotificationsEnabled}
            onCheckedChange={(checked) => {
              updateSettings({ inAppNotificationsEnabled: checked });
            }}
          />
        }
      />
    </>
  );
}
